import React, { useState, useEffect } from 'react';
import { PlusIcon, MagnifyingGlassIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import CompanyListTable from './CompanyListTable';
import CompanyDetailModal from './CompanyDetailModal';
import CompanyFormModal from './CompanyFormModal';
import CompanyDeleteConfirmModal from './CompanyDeleteConfirmModal';
import CompanyBulkActionsBar from './CompanyBulkActionsBar';
import CompanyErrorBoundary from './CompanyErrorBoundary';
import MetricCard from '../MetricCard';
import {
  getCompaniesForAdmin,
  createCompany,
  updateCompany,
  deleteCompany,
  bulkDeleteCompanies,
  exportCompanies,
  getCompanyDeletionImpact
} from '../../../services/firebase/companies';

export default function CompanyManagementView() {
  // State management
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [metrics, setMetrics] = useState({
    totalCompanies: 0,
    activeCompanies: 0,
    companiesWithUsers: 0
  });

  // Modal states
  const [modals, setModals] = useState({
    create: false,
    edit: false,
    delete: false
  });
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [deleteConfirmData, setDeleteConfirmData] = useState({
    company: null,
    cascadeInfo: null
  });

  // Loading states
  const [operationLoading, setOperationLoading] = useState({
    create: false,
    update: false,
    delete: false
  });

  // Fetch companies data
  useEffect(() => {
    fetchCompanies();
  }, []);

  // Filter and sort companies
  useEffect(() => {
    let filtered = [...companies];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(company =>
        company.name?.toLowerCase().includes(term) ||
        company.industry?.toLowerCase().includes(term) ||
        company.location?.toLowerCase().includes(term)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';

      // Handle special cases for sorting
      if (sortField === 'createdAt') {
        aValue = a.createdAt?.seconds || 0;
        bValue = b.createdAt?.seconds || 0;
      } else if (sortField === 'userCount' || sortField === 'jobCount') {
        aValue = parseInt(aValue) || 0;
        bValue = parseInt(bValue) || 0;
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredCompanies(filtered);
  }, [companies, searchTerm, sortField, sortDirection]);

  const fetchCompanies = async (showToast = true) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await getCompaniesForAdmin({
        limit: 100,
        includeStats: true
      });

      if (result.success) {
        setCompanies(result.data.companies);
        setRetryCount(0); // Reset retry count on success
        
        // Calculate metrics
        const totalCompanies = result.data.total || result.data.companies.length;
        const activeCompanies = result.data.companies.filter(c => 
          c.userCount > 0 || c.jobCount > 0
        ).length;
        const companiesWithUsers = result.data.companies.filter(c => 
          c.userCount > 0
        ).length;

        setMetrics({
          totalCompanies,
          activeCompanies,
          companiesWithUsers
        });
      } else {
        const errorMessage = result.error || 'Failed to fetch companies';
        setError(errorMessage);
        if (showToast) {
          toast.error(errorMessage);
        }
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      const errorMessage = error.message || 'Failed to fetch companies';
      setError(errorMessage);
      if (showToast) {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    fetchCompanies(false);
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleCompanyCreate = async (companyData) => {
    setOperationLoading(prev => ({ ...prev, create: true }));
    try {
      const result = await createCompany(companyData);
      if (result.success) {
        await fetchCompanies(); // Refresh the list
        setModals(prev => ({ ...prev, create: false }));
        toast.success('Company created successfully');
      } else {
        toast.error(result.error || 'Failed to create company');
      }
    } catch (error) {
      console.error('Error creating company:', error);
      toast.error('Failed to create company');
    } finally {
      setOperationLoading(prev => ({ ...prev, create: false }));
    }
  };

  const handleCompanyUpdate = async (companyData) => {
    if (!selectedCompany) return;

    setOperationLoading(prev => ({ ...prev, update: true }));
    try {
      const result = await updateCompany(selectedCompany.id, companyData);
      if (result.success) {
        await fetchCompanies(); // Refresh the list
        setModals(prev => ({ ...prev, edit: false }));
        setSelectedCompany(null);
        toast.success('Company updated successfully');
      } else {
        toast.error(result.error || 'Failed to update company');
      }
    } catch (error) {
      console.error('Error updating company:', error);
      toast.error('Failed to update company');
    } finally {
      setOperationLoading(prev => ({ ...prev, update: false }));
    }
  };

  const handleCompanyEdit = (company) => {
    setSelectedCompany(company);
    setModals(prev => ({ ...prev, edit: true }));
  };

  const handleCompanyDelete = async (companyId) => {
    const company = companies.find(c => c.id === companyId);
    if (!company) return;

    try {
      // Get cascade information
      const cascadeInfo = await getCompanyDeletionImpact(companyId);
      
      setDeleteConfirmData({
        company,
        cascadeInfo
      });
      setModals(prev => ({ ...prev, delete: true }));
    } catch (error) {
      console.error('Error getting deletion impact:', error);
      // Still show delete modal without cascade info
      setDeleteConfirmData({
        company,
        cascadeInfo: null
      });
      setModals(prev => ({ ...prev, delete: true }));
    }
  };

  const confirmCompanyDelete = async () => {
    if (!deleteConfirmData.company) return;

    setOperationLoading(prev => ({ ...prev, delete: true }));
    try {
      const result = await deleteCompany(deleteConfirmData.company.id);
      if (result.success) {
        await fetchCompanies(); // Refresh the list
        setModals(prev => ({ ...prev, delete: false }));
        setDeleteConfirmData({ company: null, cascadeInfo: null });
        toast.success('Company deleted successfully');
      } else {
        toast.error(result.error || 'Failed to delete company');
      }
    } catch (error) {
      console.error('Error deleting company:', error);
      toast.error('Failed to delete company');
    } finally {
      setOperationLoading(prev => ({ ...prev, delete: false }));
    }
  };

  const handleBulkDelete = async (companyIds) => {
    try {
      const result = await bulkDeleteCompanies(companyIds);
      if (result.success) {
        await fetchCompanies(); // Refresh the list
        setSelectedCompanies([]);
        
        if (result.data.failed.length > 0) {
          toast.error(`${result.data.successful} companies deleted, ${result.data.failed.length} failed`);
        } else {
          toast.success(`Successfully deleted ${result.data.successful} companies`);
        }
      } else {
        toast.error(result.error || 'Failed to delete companies');
      }
    } catch (error) {
      console.error('Error in bulk delete:', error);
      toast.error('Failed to delete companies');
    }
  };

  const handleBulkExport = async (companyIds) => {
    try {
      const result = await exportCompanies(companyIds);
      if (result.success) {
        // Create and download CSV file
        const csvContent = convertToCSV(result.data.companies);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `companies_export_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success(`Successfully exported ${result.data.companies.length} companies`);
      } else {
        toast.error(result.error || 'Failed to export companies');
      }
    } catch (error) {
      console.error('Error in bulk export:', error);
      toast.error('Failed to export companies');
    }
  };

  const convertToCSV = (companies) => {
    const headers = ['Name', 'Industry', 'Location', 'Website', 'Company Size', 'Founded Year', 'Users', 'Jobs', 'Created At'];
    const csvRows = [headers.join(',')];

    companies.forEach(company => {
      const row = [
        `"${company.name || ''}"`,
        `"${company.industry || ''}"`,
        `"${company.location || ''}"`,
        `"${company.website || ''}"`,
        `"${company.companySize || ''}"`,
        company.foundedYear || '',
        company.userCount || 0,
        company.jobCount || 0,
        company.createdAt?.seconds ? new Date(company.createdAt.seconds * 1000).toISOString() : ''
      ];
      csvRows.push(row.join(','));
    });

    return csvRows.join('\n');
  };

  const closeModals = () => {
    setModals({
      create: false,
      edit: false,
      delete: false
    });
    setSelectedCompany(null);
    setDeleteConfirmData({ company: null, cascadeInfo: null });
  };

  // Error state rendering
  if (error && !loading && companies.length === 0) {
    return (
      <div className="min-h-96 flex items-center justify-center">
        <div className="text-center">
          <ExclamationCircleIcon className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Failed to load companies
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {error}
          </p>
          <div className="mt-6">
            <button
              type="button"
              onClick={handleRetry}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              Try Again {retryCount > 0 && `(${retryCount})`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <CompanyErrorBoundary>
      <div className="space-y-6">
        {/* Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <MetricCard
            title="Total Companies"
            value={metrics.totalCompanies}
            loading={loading}
          />
          <MetricCard
            title="Active Companies"
            value={metrics.activeCompanies}
            subtitle="With users or jobs"
            loading={loading}
          />
          <MetricCard
            title="Companies with Users"
            value={metrics.companiesWithUsers}
            loading={loading}
          />
        </div>

      {/* Header and Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="flex-1 max-w-lg">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search companies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
            />
          </div>
        </div>
        
        <button
          onClick={() => setModals(prev => ({ ...prev, create: true }))}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Company
        </button>
      </div>

      {/* Bulk Actions */}
      <CompanyBulkActionsBar
        selectedCompanies={selectedCompanies}
        onBulkDelete={handleBulkDelete}
        onBulkExport={handleBulkExport}
        onClearSelection={() => setSelectedCompanies([])}
        loading={loading}
      />

      {/* Companies Table */}
      <CompanyListTable
        companies={filteredCompanies}
        selectedCompanies={selectedCompanies}
        onCompanySelect={(companyId) => {
          if (selectedCompanies.includes(companyId)) {
            setSelectedCompanies(prev => prev.filter(id => id !== companyId));
          } else {
            setSelectedCompanies(prev => [...prev, companyId]);
          }
        }}
        onCompanyEdit={handleCompanyEdit}
        onCompanyDelete={handleCompanyDelete}
        onSort={handleSort}
        sortField={sortField}
        sortDirection={sortDirection}
        onBulkSelect={setSelectedCompanies}
        loading={loading}
      />

      {/* Modals */}
      <CompanyFormModal
        isOpen={modals.create}
        onClose={closeModals}
        onCreate={handleCompanyCreate}
        loading={operationLoading.create}
      />

      <CompanyDetailModal
        company={selectedCompany}
        isOpen={modals.edit}
        onClose={closeModals}
        onSave={handleCompanyUpdate}
        loading={operationLoading.update}
      />

      <CompanyDeleteConfirmModal
        company={deleteConfirmData.company}
        isOpen={modals.delete}
        onClose={closeModals}
        onConfirm={confirmCompanyDelete}
        loading={operationLoading.delete}
        cascadeInfo={deleteConfirmData.cascadeInfo}
      />
      </div>
    </CompanyErrorBoundary>
  );
}